import React, { useState, useEffect } from 'react';
import Loader from '../../Shared/Loader';

import '../../../css/AddCouponModal.css';
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';


// --- Helper Types for API data ---
interface Center {
  _id: string;
  name: string;
}

interface Cluster {
  _id: string;
  appointment_cluster_id: string;
  center_id: string;
  start_date: string;
  end_date: string;
}

// --- Helper function to format date for display ---
const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  // Handles date strings like '2025-11-03'
  const date = new Date(dateString + 'T00:00:00'); // Assume local timezone
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};


interface AddCouponModalProps {
  onClose: () => void;
}

const AddCouponModal: React.FC<AddCouponModalProps> = ({ onClose }) => {
  const [form, setForm] = useState({
    code: '',
    type: 'Promo code',
    startDate: '',
    endDate: '',
    feeDiscount: '',
    redemptionLimit: '',
    venueId: '',
    clusterId: '',
  });

  const [centers, setCenters] = useState<Center[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- Effect to fetch centers on component mount ---
  useEffect(() => {
    const fetchCenters = async () => {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;
      try {
        const response = await api.center_api.get("/api/v1/centers", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data && response.data.success) {
          setCenters(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch centers:", error);
        showToast("Could not load the list of venues.", 'error');
      }
    };
    fetchCenters();
  }, []);

  // --- Effect to fetch clusters using the NEW API ---
  useEffect(() => {
    if (!form.venueId) {
      setClusters([]);
      return;
    }

    const fetchClusters = async () => {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      setIsLoading(true);
      try {
        // ✅ **MODIFICATION: Using /api/released-slots to calculate clusters**
        const response = await api.appointment_api.get(
          `/api/released-slots`, {
          headers: { Authorization: `Bearer ${token}` },
        }
        );

        if (response.data) {
          const slots = response.data.data || response.data;
          
          // Filter slots by selected venue
          const filteredSlots = Array.isArray(slots) ? slots.filter((slot: any) => slot.center_id === form.venueId) : [];

          const uniqueClustersMap = new Map<string, Cluster>();
          filteredSlots.forEach((slot: any) => {
            const clusterId = slot.appointment_cluster_id;
            if (clusterId) {
                if (!uniqueClustersMap.has(clusterId)) {
                    uniqueClustersMap.set(clusterId, {
                        _id: clusterId,
                        appointment_cluster_id: clusterId,
                        center_id: slot.center_id,
                        start_date: slot.date,
                        end_date: slot.date,
                    });
                } else {
                    const existing = uniqueClustersMap.get(clusterId)!;
                    if (slot.date < existing.start_date) existing.start_date = slot.date;
                    if (slot.date > existing.end_date) existing.end_date = slot.date;
                }
            }
          });

          const uniqueClusters = Array.from(uniqueClustersMap.values());
          setClusters(uniqueClusters);
        } else {
          setClusters([]); // Reset if the response is not as expected
        }

      } catch (error) {
        console.error("Failed to fetch appointment clusters:", error);
        showToast("Could not load appointment clusters for the selected venue.", 'error');
        setClusters([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClusters();
  }, [form.venueId]);

  // --- Unified handler for all form inputs ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setForm(prev => {
      const newState = { ...prev, [name]: value };

      if (name === 'type') {
        if (value === 'Venue code') {
          newState.startDate = '';
          newState.endDate = '';
        } else {
          newState.venueId = '';
          newState.clusterId = '';
          setClusters([]);
        }
      }

      if (name === 'venueId') {
        newState.clusterId = '';
      }

      return newState;
    });
  };

  // --- Handler for form submission (no changes needed here) ---
  const handleSubmit = async () => {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      showToast("Authentication error: You are not logged in.", 'error');
      return;
    }

    const adminData = sessionStorage.getItem("backendResponse");
    const admin = adminData ? JSON.parse(adminData) : null;
    const updatedByUserId = admin?._id;

    if (!updatedByUserId) {
      showToast("Admin ID missing. Please login again.", 'error');
      return;
    }

    let payload: any = {
      code: form.code.toUpperCase(),
      fee_discount: Number(form.feeDiscount) || 0,
      redemption_limit: form.redemptionLimit ? Number(form.redemptionLimit) : undefined,
      created_by_user_id: updatedByUserId, // This should be replaced with actual user ID
    };

    if (form.type === 'Venue code') {
      const selectedCluster = clusters.find(c => c._id === form.clusterId);
      if (!form.venueId || !selectedCluster) {
        showToast("Please select a venue and an appointment cluster.", 'error');
        return;
      }
      payload = {
        ...payload,
        code_type: 'VENUE',
        valid_from: selectedCluster.start_date,
        valid_until: selectedCluster.end_date,
        center_id: form.venueId,
        appointment_cluster_id: selectedCluster.appointment_cluster_id,
      };
    } else {
      if (!form.startDate || !form.endDate) {
        showToast("Please select a validity period.", 'error');
        return;
      }
      payload = {
        ...payload,
        code_type: 'PROMO',
        valid_from: form.startDate,
        valid_until: form.endDate,
      };
    }

    try {
      const response = await api.payment_api.post('/admin/promocodes', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Coupon code created:', response.data);
      showToast('Coupon code created successfully!', 'success');
      onClose();
    } catch (error) {
      console.error('Error creating coupon code:', error);
      showToast('Failed to create coupon code. Please check the console for details.', 'error');
    }
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-coupon-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Coupon Code</h2>
          <button className="global-close-btn" onClick={onClose}>✕</button>
        </div>

        <label>Coupon Code</label>
        <input
          type="text"
          name="code"
          placeholder="Enter coupon code name"
          value={form.code}
          onChange={handleChange}
        />

        <label>Code Type</label>
        <select name="type" value={form.type} onChange={handleChange}>
          <option>Promo code</option>
          <option>Venue code</option>
        </select>

        {form.type === 'Promo code' ? (
          <>
            <label>Validity Period</label>
            <div className="date-range">
              <input type="date" name="startDate" value={form.startDate} onChange={handleChange} />
              <span className="to-label">to</span>
              <input type="date" name="endDate" value={form.endDate} onChange={handleChange} />
            </div>
          </>
        ) : (
          <>
            <label>Venue</label>
            <select name="venueId" value={form.venueId} onChange={handleChange}>
              <option value="">Select a Venue</option>
              {centers.map(center => (
                <option key={center._id} value={center._id}>{center.name}</option>
              ))}
            </select>

            {form.venueId && (
              <div className="appointment-clusters-box">
                {isLoading ? <Loader size="small" text="Loading clusters..." /> : (
                  clusters.length > 0 ? (
                    clusters.map(cluster => (
                      <div key={cluster._id} className="cluster-radio-item">
                        <input
                          type="radio"
                          id={cluster._id}
                          name="clusterId"
                          value={cluster._id}
                          checked={form.clusterId === cluster._id}
                          onChange={handleChange}
                        />
                        <label htmlFor={cluster._id}>
                          {`${formatDateForDisplay(cluster.start_date)} - ${formatDateForDisplay(cluster.end_date)}`}
                        </label>
                      </div>
                    ))
                  ) : <p>No appointment clusters found for this venue.</p>
                )}
              </div>
            )}
          </>
        )}

        <label>Fee Discount</label>
        <input
          type="number"
          name="feeDiscount"
          placeholder="Enter discount price"
          value={form.feeDiscount}
          onChange={handleChange}
        />

        <label>Redemption Limit</label>
        <input
          type="number"
          name="redemptionLimit"
          placeholder="Enter limit (leave empty for unlimited)"
          value={form.redemptionLimit}
          onChange={handleChange}
        />

        <div className="modal-buttons">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button className="submit-button" onClick={handleSubmit}>Add Coupon Code</button>
        </div>
      </div>
    </div>
  );
};

export default AddCouponModal;